// src/components/GridConfiguratorModal.tsx

import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '../../../helpers'
import { Formik, Form } from 'formik'
import { StepperComponent } from '../../../assets/ts/components'
import * as Yup from 'yup'
import Select from 'react-select'

// Tipi di props, adattali al tuo contesto
type GridConfiguratorModalProps = {
  show: boolean
  onClose: () => void
  // devices disponibili, passati dal parent
  availableDevices: Array<{ id: string; name: string }>
  // callback finale: restituiamo i dati scelti
  onConfigured: (layout: number, chosenDevices: string[] | 'random') => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

// === VALIDAZIONE: due step ===
// Step 1 => richiede layout
const Step1Schema = Yup.object().shape({
  layout: Yup.number()
    .required('Seleziona un layout valido')
    .oneOf([1, 2, 4, 8, 12], 'Layout non valido!'),
})

// Step 2 => richiede radio button "manual" o "random" e, se manual, almeno 1 device
const Step2Schema = Yup.object().shape({
  selectionType: Yup.string()
    .required('Devi scegliere una modalità (manuale o random)')
    .oneOf(['manual', 'random'], 'Selezione non valida'),
  manualDevices: Yup.array()
    .of(Yup.string())
    .when('selectionType', {
      is: 'manual',
      then: Yup.array().of(Yup.string())
        .min(1, 'Seleziona almeno 1 device')
        .required(),
      otherwise: Yup.array().notRequired(),
    }),
})

export const GridConfiguratorModal: React.FC<GridConfiguratorModalProps> = ({
  show,
  onClose,
  availableDevices,
  onConfigured,
}) => {
  // Riferimento allo stepper Metronic
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)

  // Stato interno per tracciare se siamo nello step 1 o step 2
  const [currentStepIndex, setCurrentStepIndex] = useState(1)

  // Scegliamo dinamicamente lo schema di validazione
  const [validationSchema, setValidationSchema] = useState<any>(Step1Schema)

  // Valori iniziali di Formik
  const initialValues = {
    layout: '',
    selectionType: '',
    manualDevices: [] as string[],
  }

  console.log('GridConfiguratorModal mounted')

  // === Inizializzazione dello Stepper Metronic ===
  useEffect(() => {
    if (!show) {
      return
    }
    console.log('Modal is now shown – stepperRef is', stepperRef.current)
  
    // Se lo stepper è già stato inizializzato, non serve rifarlo:  
    // volendo puoi aggiungere un controllo if (stepper.current) return
  
    // 1) Inizializza
    StepperComponent.createInstances('[data-kt-stepper="true"]')
  
    // 2) Recupera l’istanza
    if (stepperRef.current) {
      const s = StepperComponent.getInstance(stepperRef.current)
      stepper.current = s || null
      console.log('Stepper totatStepsNumber:', s?.totatStepsNumber)
    }
  }, [show])  

  // Cambia schema se passo dallo step 1 allo step 2
  useEffect(() => {
    if (currentStepIndex === 1) {
      setValidationSchema(Step1Schema)
    } else {
      setValidationSchema(Step2Schema)
    }
  }, [currentStepIndex])

  // Avanza allo step successivo
  const goNext = () => {
    if (!stepper.current) return
    stepper.current.goNext()
    setCurrentStepIndex(stepper.current.currentStepIndex)
    console.log('goNext → currentStepIndex:', stepper.current.currentStepIndex)
  }

  // Torna allo step precedente
  const goPrev = () => {
    if (!stepper.current) return
    stepper.current.goPrev()
    setCurrentStepIndex(stepper.current.currentStepIndex)
    console.log('goPrev → currentStepIndex:', stepper.current.currentStepIndex)
  }

  // Chiudi il modal, resettando lo step a 1
  const handleHide = () => {
    if (stepper.current) {
      stepper.current.goto(1)
    }
    setCurrentStepIndex(1)
    onClose()
  }

  // === Gestione submit Formik ===
  const handleSubmit = async (values: typeof initialValues) => {
    if (!stepper.current) return

    console.log('handleSubmit: form values →', values)

    // Se non siamo all'ultimo step, andiamo avanti
    if (currentStepIndex !== stepper.current.totatStepsNumber) {
      goNext()
    } else {
      // Siamo allo step 2 e l'utente clicca su "Invio"
      const finalLayout = Number(values.layout)
      const chosenDevices =
        values.selectionType === 'random'
          ? 'random'
          : values.manualDevices

      console.log('Invio finale → layout:', finalLayout, ' chosenDevices:', chosenDevices)

      // Passiamo i valori scelti al parent
      onConfigured(finalLayout, chosenDevices)

      // Resettiamo e chiudiamo
      stepper.current.goto(1)
      setCurrentStepIndex(1)
      onClose()
    }
  }

  // Validazione custom: se selectionType=manual e user seleziona più device del layout, errore
  const validateManualDevices = (values: typeof initialValues) => {
    const errors: any = {}
    const layoutNumber = Number(values.layout) || 0

    if (
      values.selectionType === 'manual' &&
      values.manualDevices.length > layoutNumber
    ) {
      errors.manualDevices = `Puoi scegliere al massimo ${layoutNumber} device`
    }

    return errors
  }

  return createPortal(
    <Modal
      show={show}
      onHide={handleHide}
      dialogClassName='modal-dialog modal-dialog-centered mw-900px'
    >
      <div className='modal-header'>
        <h2>Configura griglia</h2>
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleHide}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>

      <div className='modal-body'>
        <div
          ref={stepperRef}
          data-kt-stepper='true'
          className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
        >
          {/* Colonna sinistra con la "barra" dei due step */}
          <div className='card d-flex flex-row-auto w-100 w-xl-300px w-xxl-400px me-9'>
            <div className='card-body px-6 px-lg-10 px-xxl-15 py-20'>
              <div className='stepper-nav'>
                {/* Step 1 */}
                <div
                  className={`stepper-item ${currentStepIndex === 1 ? 'current' : ''}`}
                  data-kt-stepper-element='nav'
                >
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>1</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Layout griglia</h3>
                      <div className='stepper-desc fw-semibold'></div>
                    </div>
                  </div>
                  <div className='stepper-line h-40px'></div>
                </div>

                {/* Step 2 */}
                <div
                  className={`stepper-item ${currentStepIndex === 2 ? 'current' : ''}`}
                  data-kt-stepper-element='nav'
                >
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>2</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Seleziona visori</h3>
                      <div className='stepper-desc fw-semibold'></div>
                    </div>
                  </div>
                  <div className='stepper-line h-40px'></div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonna destra con i form veri e propri */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validate={validateManualDevices}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              setFieldValue,
              handleSubmit, // se vuoi richiamarlo manualmente
            }) => {
              // LOG DI DEBUG
              console.log('Form errors:', errors)
              console.log('Form values:', values)

              return (
                <Form className='py-20 w-100 w-xl-700px px-9' noValidate>
                  {/* STEP 1 Content */}
                  {currentStepIndex === 1 && (
                    <div className='mb-10'>
                      <label className='form-label d-block mb-2'>Layout griglia</label>
                      <select
                        name='layout'
                        className='form-select form-select-solid'
                        value={values.layout}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10)
                          setFieldValue('layout', parsed)
                          console.log('layout parsed →', parsed)
                        }}
                      >
                        <option value=''>Seleziona...</option>
                        <option value='1'>1</option>
                        <option value='2'>2</option>
                        <option value='4'>4</option>
                        <option value='8'>8</option>
                        <option value='12'>12</option>
                      </select>
                      {errors.layout && touched.layout && (
                        <div className='text-danger'>{errors.layout}</div>
                      )}
                    </div>                  
                  )}

                  {/* STEP 2 Content */}
                  {currentStepIndex === 2 && (
                  <div className='current d-flex flex-column' data-kt-stepper-element='content'>
                    {/* BLOCCO 1: Selezione modalità (manuale o random) */}
                    <div className='mb-10'>
                      <label className='form-label mb-2'>Selezione visori</label>
                      <div className='d-flex gap-3'>
                        <div>
                          <input
                            type='radio'
                            className='btn-check'
                            id='selectionType_manual'
                            checked={values.selectionType === 'manual'}
                            onChange={() => setFieldValue('selectionType', 'manual')}
                          />
                          <label
                            className='btn btn-outline btn-outline-dashed btn-outline-default px-3'
                            htmlFor='selectionType_manual'
                          >
                            Manuale
                          </label>
                        </div>
                        <div>
                          <input
                            type='radio'
                            className='btn-check'
                            id='selectionType_random'
                            checked={values.selectionType === 'random'}
                            onChange={() => setFieldValue('selectionType', 'random')}
                          />
                          <label
                            className='btn btn-outline btn-outline-dashed btn-outline-default px-3'
                            htmlFor='selectionType_random'
                          >
                            Random
                          </label>
                        </div>
                      </div>
                      {errors.selectionType && (
                        <div className='text-danger mt-2'>
                          {errors.selectionType}
                        </div>
                      )}
                    </div>

                    {/* BLOCCO 2: Select multipla */}
                    {values.selectionType === 'manual' && (
                      <div className='mb-10'>
                        <label className='form-label mb-2'>Scegli quali nello specifico</label>
                        <Select
                          isMulti
                          className='form-select-solid'
                          options={availableDevices.map((dev) => ({
                            value: dev.id,
                            label: dev.name,
                          }))}
                          onChange={(selected: any) => {
                            if (Array.isArray(selected)) {
                              setFieldValue('manualDevices', selected.map((s) => s.value))
                            } else {
                              setFieldValue('manualDevices', [])
                            }
                          }}
                          value={availableDevices
                            .filter((dev) => values.manualDevices.includes(dev.id))
                            .map((dev) => ({ value: dev.id, label: dev.name }))}
                        />
                        {errors.manualDevices && (
                          <div className='text-danger mt-2'>
                            {errors.manualDevices}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                  {/* Footer con i pulsanti Avanti/Indietro/Invio */}
                  <div className='d-flex flex-stack pt-10'>
                    <div className='mr-2'>
                      {currentStepIndex > 1 && (
                        <button
                          type='button'
                          onClick={goPrev}
                          className='btn btn-lg btn-light-primary me-3'
                        >
                          <KTSVG path='/media/icons/duotune/arrows/arr063.svg' className='svg-icon-4 me-1' />
                          Indietro
                        </button>
                      )}
                    </div>
                    <div>
                      <button type='submit' className='btn btn-lg btn-primary me-3'>
                        {currentStepIndex === 1 && (
                          <>
                            Continua
                            <KTSVG
                              path='/media/icons/duotune/arrows/arr064.svg'
                              className='svg-icon-3 ms-2 me-0'
                            />
                          </>
                        )}
                        {currentStepIndex === 2 && 'Invio'}
                      </button>
                    </div>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}